import * as React from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import axios from 'axios';
import { getCookies } from "cookies-next";
import {
  ClassicEditor,
  AccessibilityHelp,
  Alignment,
  Autoformat,
  AutoLink,
  Autosave,
  BlockQuote,
  Bold,
  Code,
  Essentials,
  FindAndReplace,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  GeneralHtmlSupport,
  Heading,
  Highlight,
  HorizontalLine,
  Indent,
  IndentBlock,
  Italic,
  Link,
  Paragraph,
  RemoveFormat,
  SelectAll,
  ShowBlocks,
  SourceEditing,
  SpecialCharacters,
  SpecialCharactersArrows,
  SpecialCharactersCurrency,
  SpecialCharactersEssentials,
  SpecialCharactersLatin,
  SpecialCharactersMathematical,
  SpecialCharactersText,
  Strikethrough,
  Style,
  Subscript,
  Superscript,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  Underline,
  Undo
} from 'ckeditor5';

import 'ckeditor5/ckeditor5.css';
import { config } from '../../data/config';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

// Optional: Import Farsi locale if available
// import 'ckeditor5/translations/fa';

type LanguageCode = string;

export default function TissText({ data, reload, activeLang = 'en' }: { data: any; reload: () => void; activeLang?: LanguageCode }) {
  const { t } = useTranslation();
  const cookie = getCookies();

  // Helper to get multilingual value
  const getMultilingualValue = (field: any, lang: LanguageCode, fallback: string = ''): string => {
    if (!field) return fallback;
    if (typeof field === 'string') return field; // Legacy support
    if (typeof field === 'object') {
      return field[lang] || field.en || field.fa || field.ku || field.ar || field.tr || fallback;
    }
    return fallback;
  };

  // Helper to set multilingual value
  const setMultilingualValue = (currentData: any, lang: LanguageCode, value: string): any => {
    if (!currentData || typeof currentData !== 'object') {
      return { [lang]: value };
    }
    return {
      ...currentData,
      [lang]: value
    };
  };

  // Get current HTML value for the active language
  const getCurrentHtmlForLang = () => {
    const htmlField = data?.data?.html || {};
    return getMultilingualValue(htmlField, activeLang);
  };

  const initialHtml = getCurrentHtmlForLang();

  // Initialize multilingual data from props
  const initializeMultilingualData = () => {
    const htmlField = data?.data?.html || {};
    if (typeof htmlField === 'object' && htmlField !== null && !Array.isArray(htmlField)) {
      return { ...htmlField };
    } else if (typeof htmlField === 'string' && htmlField) {
      return { en: htmlField };
    }
    return {};
  };

  // State - Store ALL multilingual data in state (all 5 languages at once)
  const [multilingualHtml, setMultilingualHtml] = React.useState<any>(initializeMultilingualData());
  
  // Get current language HTML from multilingual state
  const currentHtml = multilingualHtml[activeLang] || '';
  const hasContent = !!currentHtml || Object.values(multilingualHtml).some((val: any) => !!val);
  
  // Debug logging
  React.useEffect(() => {
    console.log('=== TissText Language Change ===');
    console.log('Active Language:', activeLang);
    console.log('Current HTML:', currentHtml);
    console.log('All Multilingual HTML:', multilingualHtml);
    console.log('Has Content:', hasContent);
  }, [activeLang, currentHtml, multilingualHtml, hasContent]);
  
  const [isEditing, setIsEditing] = React.useState(!hasContent);
  const [saving, setSaving] = React.useState(false);
  const [hasChanges, setHasChanges] = React.useState(false);
  const [translating, setTranslating] = React.useState(false);
  const prevLangRef = React.useRef(activeLang);
  const prevDataIdRef = React.useRef(data?._id);
  const editorRef = React.useRef<any>(null);

  // Supported languages
  const supportedLanguages = ['en', 'fa', 'ar', 'krd', 'tr'];
  const languageNames: Record<string, string> = {
    en: 'English',
    fa: 'فارسی',
    ar: 'العربية',
    krd: 'Kurdî',
    tr: 'Türkçe'
  };

  // Update multilingual state when data from server changes
  React.useEffect(() => {
    if (prevDataIdRef.current !== data?._id) {
      const htmlField = data?.data?.html || {};
      
      // Initialize multilingual data from server
      let newMultilingualData: any;
      if (typeof htmlField === 'object' && htmlField !== null && !Array.isArray(htmlField)) {
        // Start with server data, then merge any unsaved translations from state
        // This ensures server data is primary, but we preserve any unsaved work
        newMultilingualData = { ...htmlField };
        // Only preserve state data if it's not empty and server doesn't have it
        Object.keys(multilingualHtml).forEach(lang => {
          if (multilingualHtml[lang] && !newMultilingualData[lang]) {
            newMultilingualData[lang] = multilingualHtml[lang];
          }
        });
      } else if (typeof htmlField === 'string' && htmlField) {
        // Legacy format - preserve existing languages, add this as 'en'
        newMultilingualData = { ...multilingualHtml, en: htmlField };
      } else {
        // Empty - keep existing multilingual data if it exists
        newMultilingualData = Object.keys(multilingualHtml).length > 0 
          ? { ...multilingualHtml } 
          : {};
      }
      
      console.log('Updating multilingual data from server:', {
        serverData: htmlField,
        currentState: multilingualHtml,
        newState: newMultilingualData
      });
      
      setMultilingualHtml(newMultilingualData);
      setHasChanges(false);
      prevDataIdRef.current = data?._id;
    }
  }, [data?._id, data?.data?.html]);

  // Update when language changes - reset hasChanges but keep multilingual state
  React.useEffect(() => {
    if (prevLangRef.current !== activeLang) {
      setHasChanges(false);
      prevLangRef.current = activeLang;
      // Update editor content when language changes
      if (editorRef.current && isEditing) {
        const newHtml = multilingualHtml[activeLang] || '';
        editorRef.current.setData(newHtml);
      }
    }
  }, [activeLang, isEditing, multilingualHtml]);

  // Track changes - compare editor content with multilingual state
  // This will be handled in handleEditorChange

  // Save handler - save ALL languages at once
  const saveContent = async () => {
    if (!data?._id) return;

    setSaving(true);
    try {
      console.log('=== SAVING ALL MULTILINGUAL HTML ===');
      console.log('Component ID:', data._id);
      console.log('Current language:', activeLang);
      console.log('All languages in state:', multilingualHtml);
      console.log('Languages count:', Object.keys(multilingualHtml).length);
      
      // Send ALL multilingual data to backend
      const dataToSend = {
        html: multilingualHtml // Send complete multilingual object
      };
      
      console.log('Sending to backend:', JSON.stringify(dataToSend, null, 2));
      
      const response = await axios.put(
        `${config.url}/v1/dashboard/component/texteditor/${data._id}`,
        dataToSend,
        {
          headers: { Authorization: 'Bearer ' + cookie['token'] },
        }
      );
      
      console.log('Server response:', response.data);
      
      Swal.fire({
        title: t('Saved successfully'),
        icon: 'success',
        timer: 1200,
        showConfirmButton: false,
      });
      setIsEditing(false);
      setHasChanges(false);
      
      // Reload to get fresh data
      reload();
    } catch (error: any) {
      console.error('Save failed:', error);
      console.error('Error response:', error.response?.data);
      Swal.fire({
        title: t('Error saving'),
        text: error.response?.data?.message || error.message,
        icon: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // Translation helper function - extracts text from HTML and translates it
  const translateHtmlContent = async (html: string, fromLang: string, toLang: string): Promise<string> => {
    if (!html || html.trim() === '') return '';

    try {
      // Method 1: Try backend translation API if available
      try {
        const response = await axios.post(
          `${config.url}/v1/translate`,
          {
            text: html,
            from: fromLang === 'krd' ? 'ku' : fromLang,
            to: toLang === 'krd' ? 'ku' : toLang,
            format: 'html'
          },
          {
            headers: { Authorization: 'Bearer ' + cookie['token'] },
          }
        );
        if (response.data && response.data.translatedText) {
          return response.data.translatedText;
        }
      } catch (backendError) {
        // Backend translation not available, fall back to client-side
        console.log('Backend translation not available, using client-side translation');
      }

      // Method 2: Client-side translation using Google Translate (free endpoint)
      // Create a temporary DOM element to parse HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;

      // Block-level elements that should be translated as complete units
      const blockElements = ['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'TD', 'TH', 'BLOCKQUOTE', 'FIGCAPTION'];
      
      // Find all block-level elements and translate their full text content
      const elementsToTranslate: { element: HTMLElement; originalText: string; isBlock: boolean }[] = [];
      
      // First, find all block elements and get their full text content
      blockElements.forEach(tagName => {
        const elements = tempDiv.querySelectorAll(tagName);
        elements.forEach((el) => {
          const htmlEl = el as HTMLElement;
          // Get the full text content of this element (including nested inline elements)
          const textContent = htmlEl.textContent?.trim() || '';
          
          if (textContent && textContent.length > 0) {
            // Check if this element is nested inside another block element we're already translating
            let isNested = false;
            for (const existing of elementsToTranslate) {
              if (existing.element.contains(htmlEl) && existing.isBlock) {
                isNested = true;
                break;
              }
            }
            
            if (!isNested) {
              elementsToTranslate.push({ 
                element: htmlEl, 
                originalText: textContent,
                isBlock: true
              });
            }
          }
        });
      });

      // Also handle DIV elements that contain text (but be careful with nested blocks)
      const divElements = tempDiv.querySelectorAll('DIV');
      divElements.forEach((el) => {
        const htmlEl = el as HTMLElement;
        // Check if this div contains block elements
        const hasBlockChildren = Array.from(htmlEl.children).some(
          child => blockElements.includes(child.tagName)
        );
        
        // If it doesn't have block children, it might be a text container
        if (!hasBlockChildren) {
          const textContent = htmlEl.textContent?.trim() || '';
          if (textContent && textContent.length > 0) {
            // Check if it's nested
            let isNested = false;
            for (const existing of elementsToTranslate) {
              if (existing.element.contains(htmlEl) && existing.isBlock) {
                isNested = true;
                break;
              }
            }
            
            if (!isNested) {
              elementsToTranslate.push({ 
                element: htmlEl, 
                originalText: textContent,
                isBlock: false
              });
            }
          }
        }
      });

      // Translate each element's full text content
      for (const { element, originalText } of elementsToTranslate) {
        try {
          if (!originalText || originalText.trim().length === 0) continue;
          
          // Skip if text is too long (might hit API limits)
          if (originalText.length > 4000) {
            console.warn(`Text too long for translation (${originalText.length} chars), skipping`);
            continue;
          }
          
          console.log(`Translating block element (${element.tagName}, ${originalText.length} chars):`, originalText.substring(0, 150) + (originalText.length > 150 ? '...' : ''));
          
          // Use Google Translate API (free version via URL)
          // Note: The API might split long text, so we need to handle that
          const response = await fetch(
            `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${fromLang === 'krd' ? 'ku' : fromLang}&tl=${toLang === 'krd' ? 'ku' : toLang}&dt=t&q=${encodeURIComponent(originalText)}`
          );
          
          if (!response.ok) {
            console.warn(`Translation API error for element: ${response.status}`);
            continue;
          }
          
          const result = await response.json();
          
          // The API returns an array of translations - combine them all
          let translatedText = '';
          if (Array.isArray(result) && result[0]) {
            // result[0] is an array of translation segments
            translatedText = result[0]
              .map((segment: any) => segment?.[0] || '')
              .filter((text: string) => text && text.trim().length > 0)
              .join(' ')
              .trim();
          }
          
          // Fallback if translation failed
          if (!translatedText || translatedText.length === 0) {
            translatedText = originalText;
            console.warn('Translation returned empty, keeping original');
          }
          
          console.log(`Translated to (${translatedText.length} chars):`, translatedText.substring(0, 150) + (translatedText.length > 150 ? '...' : ''));
          
          // Replace text content while preserving HTML structure
          // Strategy: Replace all text nodes with the translated text, but keep HTML elements
          const childNodes = Array.from(element.childNodes);
          const hasHtmlElements = childNodes.some(node => 
            node.nodeType === Node.ELEMENT_NODE && 
            !['BR', 'HR'].includes((node as HTMLElement).tagName)
          );
          
          if (hasHtmlElements) {
            // Element has HTML formatting (bold, italic, links, etc.)
            // We need to replace text nodes but keep the HTML structure
            // For simplicity, we'll replace the first significant text node
            let replaced = false;
            childNodes.forEach(node => {
              if (!replaced && node.nodeType === Node.TEXT_NODE) {
                const textNode = node as Text;
                const text = textNode.textContent?.trim();
                if (text && text.length > 0) {
                  textNode.textContent = translatedText;
                  replaced = true;
                }
              }
            });
            
            // If no text node found, insert translated text at the beginning
            if (!replaced) {
              const firstTextNode = document.createTextNode(translatedText);
              element.insertBefore(firstTextNode, childNodes[0] || null);
            }
            
            // Remove other standalone text nodes (but keep text in HTML elements)
            childNodes.forEach(node => {
              if (node.nodeType === Node.TEXT_NODE) {
                const textNode = node as Text;
                const text = textNode.textContent?.trim();
                // Keep the first one we replaced, remove others that are just whitespace or duplicates
                if (text && text !== translatedText && text.length < 10) {
                  textNode.remove();
                }
              }
            });
          } else {
            // No HTML formatting, just replace all text content
            element.textContent = translatedText;
          }
          
          // Add small delay to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 300));
        } catch (error) {
          console.error(`Translation error for element (${element.tagName}):`, error);
          // Keep original text if translation fails
        }
      }

      return tempDiv.innerHTML;
    } catch (error) {
      console.error('Translation failed:', error);
      throw error;
    }
  };

  // Auto-translate handler - translates current language to all other languages
  const handleAutoTranslate = async () => {
    if (!currentHtml || currentHtml.trim() === '') {
      Swal.fire({
        title: t('No content to translate'),
        text: t('Please add content in the current language first'),
        icon: 'warning',
      });
      return;
    }

    const confirmed = await Swal.fire({
      title: t('Auto Translate'),
      text: t('This will translate the current content to all other languages. Continue?'),
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: t('Yes, translate'),
      cancelButtonText: t('Cancel'),
    });

    if (!confirmed.isConfirmed) return;

    setTranslating(true);
    try {
      const otherLanguages = supportedLanguages.filter(lang => lang !== activeLang);
      const updatedMultilingualHtml = { ...multilingualHtml };

      // Translate to each other language
      for (const targetLang of otherLanguages) {
        try {
          const translatedHtml = await translateHtmlContent(
            currentHtml,
            activeLang === 'krd' ? 'ku' : activeLang,
            targetLang === 'krd' ? 'ku' : targetLang
          );
          updatedMultilingualHtml[targetLang] = translatedHtml;
        } catch (error) {
          console.error(`Translation failed for ${targetLang}:`, error);
          Swal.fire({
            title: t('Translation error'),
            text: t(`Failed to translate to ${languageNames[targetLang] || targetLang}`),
            icon: 'error',
            timer: 2000,
          });
        }
      }

      console.log('Translation completed. Updated multilingual HTML:', updatedMultilingualHtml);
      setMultilingualHtml(updatedMultilingualHtml);
      setHasChanges(true);

      // Show summary of what was translated
      const translatedLangs = otherLanguages.filter(lang => updatedMultilingualHtml[lang]);
      Swal.fire({
        title: t('Translation completed'),
        text: t('Content has been translated to all other languages') + ` (${translatedLangs.length}/${otherLanguages.length} languages)`,
        icon: 'success',
        timer: 3000,
        showConfirmButton: false,
      });
    } catch (error: any) {
      console.error('Auto-translate failed:', error);
      Swal.fire({
        title: t('Translation failed'),
        text: error.message || t('An error occurred during translation'),
        icon: 'error',
      });
    } finally {
      setTranslating(false);
    }
  };

  // Cancel handler with confirmation if there are changes
  const handleCancel = () => {
    if (hasChanges) {
      Swal.fire({
        title: t('Discard changes?'),
        text: t('You have unsaved changes. Are you sure you want to cancel?'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: t('Yes, discard'),
        cancelButtonText: t('No, keep editing'),
      }).then((result) => {
        if (result.isConfirmed) {
          // Reload from server to reset all changes
          reload();
          setIsEditing(false);
          setHasChanges(false);
        }
      });
    } else {
      setIsEditing(false);
    }
  };

  // Editor config (memoized to prevent re-renders)
  const editorConfig = React.useMemo(() => ({
    language: 'fa',
    toolbar: {
      items: [
        'undo', 'redo', '|',
        'heading', '|',
        'bold', 'italic', 'underline', 'strikethrough', 'subscript', 'superscript', '|',
        'fontFamily', 'fontSize', 'fontColor', 'fontBackgroundColor', '|',
        'alignment', '|',
        'numberedList', 'bulletedList', 'indent', 'outdent', '|',
        'link', 'blockQuote', 'insertTable', 'horizontalLine', 'specialCharacters', '|',
        'removeFormat', 'sourceEditing'
      ],
      shouldNotGroupWhenFull: true,
    },
    plugins: [
      AccessibilityHelp,
      Alignment,
      Autoformat,
      AutoLink,
      Autosave,
      BlockQuote,
      Bold,
      Code,
      Essentials,
      FindAndReplace,
      FontBackgroundColor,
      FontColor,
      FontFamily,
      FontSize,
      GeneralHtmlSupport,
      Heading,
      Highlight,
      HorizontalLine,
      Indent,
      IndentBlock,
      Italic,
      Link,
      Paragraph,
      RemoveFormat,
      SelectAll,
      ShowBlocks,
      SourceEditing,
      SpecialCharacters,
      SpecialCharactersArrows,
      SpecialCharactersCurrency,
      SpecialCharactersEssentials,
      SpecialCharactersLatin,
      SpecialCharactersMathematical,
      SpecialCharactersText,
      Strikethrough,
      Style,
      Subscript,
      Superscript,
      Table,
      TableCaption,
      TableCellProperties,
      TableColumnResize,
      TableProperties,
      TableToolbar,
      TextTransformation,
      Underline,
      Undo
    ],
    initialData: currentHtml,
    // Enable RTL
    ui: {
      viewportTopOffset: 0
    }
  }), [activeLang]); // Re-create config when language changes

  // Handle editor change - update ONLY the current language in multilingual state
  const handleEditorChange = (_: any, editor: any) => {
    const newHtml = editor.getData();
    
    // Update only the current language, preserve all others
    setMultilingualHtml((prev: any) => ({
      ...prev, // Keep all other languages
      [activeLang]: newHtml // Update only current language
    }));
    
    // Check if there are changes
    const originalHtml = data?.data?.html || {};
    const originalForLang = typeof originalHtml === 'object' && originalHtml !== null 
      ? (originalHtml[activeLang] || '')
      : (activeLang === 'en' ? originalHtml : '');
    setHasChanges(newHtml !== originalForLang);
  };

  // Placeholder view (when no content)
  if (!hasContent && !isEditing) {
    return (
      <div
        className="w-full p-4 flex flex-col items-center justify-center cursor-pointer hover:bg-blue-50/30 bg-white transition-all duration-300 group"
        onClick={() => setIsEditing(true)}
        dir="rtl"
      >
        <div className="bg-blue-100 rounded-full p-4 mb-4 group-hover:bg-blue-200 transition-colors duration-300">
          <img src="/icons/addtext.svg" className="h-8 w-8" alt={t('Add Text')} />
        </div>
        <span className="text-gray-600 text-sm font-medium group-hover:text-blue-600 transition-colors duration-300">
          {t('Click to add text')}
        </span>
        <span className="text-gray-400 text-xs mt-1">
          {t('Start typing to create content')}
        </span>
      </div>
    );
  }

  // Edit Mode
  if (isEditing) {
    return (
      <div className="w-full bg-white overflow-hidden transition-all duration-300" dir="rtl">
        {/* Editor Content */}
        <div className="p-0.5">
          <div className="overflow-hidden">
            <CKEditor
              key={`editor-${data?._id}-${activeLang}`}
              editor={ClassicEditor}
              // @ts-ignore
              config={editorConfig}
              data={currentHtml}
              onChange={handleEditorChange}
              onReady={(editor: any) => {
                editorRef.current = editor;
                // Ensure editor has the correct data for current language
                const langHtml = multilingualHtml[activeLang] || '';
                if (editor.getData() !== langHtml) {
                  editor.setData(langHtml);
                }
              }}
            />
          </div>

          {/* Action Buttons */}
          <div className="mt-4 flex gap-3 justify-end items-center flex-wrap">
            {hasChanges && (
              <span className="text-xs text-orange-600 bg-orange-100 px-3 py-1.5 rounded-full font-medium">
                {t('Unsaved changes')}
              </span>
            )}
            <button
              type="button"
              onClick={handleAutoTranslate}
              disabled={translating || saving || !currentHtml || currentHtml.trim() === ''}
              className="px-5 py-2.5 border border-purple-300 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 hover:border-purple-400 text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              title={t('Auto translate to all other languages')}
            >
              {translating ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>{t('Translating...')}</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.036 9.5a18.022 18.022 0 019.568-9M6.412 9a17.982 17.982 0 011.564-2.5m0 0A17.982 17.982 0 0112 3c2.5 0 4.847.655 6.888 1.8M6.412 9.5c.676.5 1.404.921 2.176 1.256M12 21a18.022 18.022 0 005.588-9M12 21c-2.5 0-4.847-.655-6.888-1.8m9.776 0a17.982 17.982 0 01-1.564 2.5m0 0A17.982 17.982 0 0112 21m-4.412-1.5c.676-.5 1.404-.921 2.176-1.256" />
                  </svg>
                  <span>{t('Auto Translate')}</span>
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving || translating}
              className="px-5 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 text-sm font-medium text-gray-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              {t('Cancel')}
            </button>
            <button
              type="button"
              onClick={saveContent}
              disabled={saving || translating || !hasChanges}
              className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg"
            >
              {saving ? (
                <>
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>{t('Saving...')}</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{t('Save')}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Preview Mode
  return (
    <div className="w-full bg-white transition-all duration-300 group relative" dir="rtl">
      <div className="p-0.5 relative">
        {currentHtml ? (
          <div 
            className="text-right prose prose-sm max-w-none prose-headings:font-semibold prose-p:text-gray-700 prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline"
            dangerouslySetInnerHTML={{ __html: currentHtml }} 
          />
        ) : (
          <div className="text-gray-400 italic py-8 text-center">
            <div className="flex flex-col items-center">
              <svg className="w-12 h-12 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>{t('متنی وجود ندارد')}</span>
            </div>
          </div>
        )}

        {/* Edit Button - Appears on hover */}
        <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
          <button
            onClick={() => setIsEditing(true)}
            className="bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-600 shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {t('Edit text')}
          </button>
        </div>
      </div>
    </div>
  );
}